import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';
import { FaEtsy } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { trackProjectActClick } from '../lib/analytics';
import { getPath, routes } from '../config/routeMap';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { language, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Language-aware navigation targets, built from the routeMap.
  const navLinks = [
    { key: 'home',       to: getPath('home', language),       label: t('nav.home') },
    { key: 'products',   to: getPath('products', language),   label: t('nav.products') },
    { key: 'projectAct', to: getPath('projectAct', language), label: t('nav.projectAct'), highlight: true },
    { key: 'story',      to: getPath('story', language),      label: t('nav.story') },
    { key: 'healthcare', to: getPath('healthcare', language), label: t('nav.healthcare') },
    { key: 'faq',        to: getPath('faq', language),        label: 'FAQ' },
    { key: 'guides',     to: getPath('guides', language),     label: 'Guides' },
    { key: 'contact',    to: getPath('contact', language),    label: t('nav.contact') },
  ];

  const homePath = getPath('home', language);
  const homeLabel = language === 'fr' ? 'MyGuardPod \u2014 Accueil' : 'MyGuardPod \u2014 Home';

  // Determine active state by checking if location.pathname matches EITHER
  // the current language variant OR the other language variant of the same route.
  const isActive = (routeKey) => {
    const entry = routes[routeKey];
    if (!entry) return false;
    const normalized = location.pathname.replace(/\/$/, '') || '/';
    return normalized === entry.en || normalized === entry.fr;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#1a1a1a]/95 backdrop-blur-xl shadow-lg'
          : 'bg-[#1a1a1a]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to={homePath}
            className="flex items-center group"
            data-testid="logo-link"
            aria-label={homeLabel}
          >
            <img
              src="/images/LOGO_MGP.webp"
              alt={homeLabel}
              className="h-8 sm:h-10 lg:h-12 w-auto"
              width="240"
              height="60"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => {
              const active = isActive(link.key);
              if (link.highlight) {
                return (
                  <Link
                    key={link.key}
                    to={link.to}
                    onClick={() => trackProjectActClick('nav_desktop')}
                    className={`relative font-manrope text-sm font-semibold transition-all duration-300 px-3 py-1.5 rounded-full border ${
                      active
                        ? 'bg-[#E31B23] text-white border-[#E31B23]'
                        : 'border-[#E31B23]/60 text-[#E31B23] hover:bg-[#E31B23] hover:text-white hover:border-[#E31B23]'
                    }`}
                    data-testid={`nav-${link.key}`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      {link.label}
                    </span>
                  </Link>
                );
              }
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  className={`font-manrope text-sm font-medium transition-colors ${
                    active ? 'text-[#E31B23]' : 'text-neutral-300 hover:text-white'
                  }`}
                  data-testid={`nav-${link.key}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <LanguageSwitcher testId="lang-selector" />

            <a
              href="https://www.etsy.com/shop/MyGuardPod"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 active:scale-95"
              data-testid="etsy-cta"
            >
              <FaEtsy className="w-4 h-4" />
              <span>{t('nav.shopEtsy')}</span>
            </a>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? (language === 'fr' ? 'Fermer le menu' : 'Close menu') : (language === 'fr' ? 'Ouvrir le menu' : 'Open menu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen
                ? <X className="w-6 h-6 text-white" />
                : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#1a1a1a] border-t border-neutral-800"
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                const active = isActive(link.key);
                if (link.highlight) {
                  return (
                    <Link
                      key={link.key}
                      to={link.to}
                      onClick={() => trackProjectActClick('nav_mobile')}
                      className={`block px-4 py-3 rounded-lg font-semibold transition-colors border ${
                        active
                          ? 'bg-[#E31B23] text-white border-[#E31B23]'
                          : 'border-[#E31B23]/60 text-[#E31B23] hover:bg-[#E31B23]/10'
                      }`}
                      data-testid={`mobile-nav-${link.key}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                        {link.label}
                      </span>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={link.key}
                    to={link.to}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                      active ? 'bg-[#E31B23]/10 text-[#E31B23]' : 'text-neutral-300 hover:bg-white/5'
                    }`}
                    data-testid={`mobile-nav-${link.key}`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <a
                href="https://www.etsy.com/shop/MyGuardPod"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 bg-[#E31B23] text-white px-4 py-3 rounded-lg font-medium mt-4"
                data-testid="mobile-etsy-cta"
              >
                <FaEtsy className="w-5 h-5" />
                <span>{t('nav.shopEtsy')}</span>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
